/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2025 ICEMEDX, Inc.
***********************************************/
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { tap, map } from 'rxjs/operators';
import { XmlparseService } from '../xml/xmlparse.service';

import { AlertService } from "../../services/alert/alert.service";
import { LoginService } from '../../login/login.service';
import { IceTypes } from '../../models/icetypes';
import { notebookpage } from '../../models/customforms/notebookpage';
import { notebookpagetotype } from '../../models/customforms/notebookpagetotype';

declare var require: any;

@Injectable()
export class CustomFormService extends XmlparseService {

  constructor(private http: HttpClient, private alertSvc: AlertService, private loginSvc: LoginService) {
    super(alertSvc, loginSvc);
  }

  /////////////////////////////
  // API Operations
  public getForms(api: string, act: string, acttype: IceTypes.ACCOUNT_TYPE, srctype: IceTypes.SourceType, subtype: number = -1): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Form service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";
    if (api == null || api.length == 0) throw "API input invalid.";

    let url = envs[currentEnv].icemedx_api + "/" + api + "/default.aspx?domain=" + act + "&data=forms&domaintype=" + acttype.toString() + "&key=&subkeys=" + "srctype:" + srctype.toString() + ";subtype:" + subtype.toString();
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseForms(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getForm(api: string, act: string, acttype: IceTypes.ACCOUNT_TYPE, sample: boolean, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Form service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";
    if (key == null || key.length == 0) throw "Key input invalid";
    if (api == null || api.length == 0) throw "API input invalid.";

    let url = envs[currentEnv].icemedx_api + "/" + api + "/default.aspx?domain=" + act + "&data=form&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=sample:" + sample.toString();
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseForm(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getFormRaw(api: string, act: string, acttype: IceTypes.ACCOUNT_TYPE, sample: boolean, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Form service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";
    if (key == null || key.length == 0) throw "Key input invalid";
    if (api == null || api.length == 0) throw "API input invalid.";

    let url = envs[currentEnv].icemedx_api + "/" + api + "/default.aspx?domain=" + act + "&data=form&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=sample:" + sample.toString();
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => response
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getFormTypes(api: string, act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string, sample: boolean): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Form service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";
    if (api == null || api.length == 0) throw "API input invalid.";

    let url = envs[currentEnv].icemedx_api + "/" + api + "/default.aspx?domain=" + act + "&data=formtypes&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=" + "&subkeys=sample:" + sample.toString();;
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseFormType(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public insertUpdateForm(api: string, act: string, acttype: IceTypes.ACCOUNT_TYPE, nbp: notebookpage): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Form service is not configured.";
    if (nbp == null) throw "Form object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";
    if (api == null || api.length == 0) throw "API input invalid.";

    nbp.notebook_page_key = nbp.notebook_page_key.trim();

    let url = envs[currentEnv].icemedx_api + "/" + api + "/default.aspx?domain=" + act + "&data=form&domaintype=" + acttype.toString() + "&key=" + nbp.notebook_page_key + "&subkeys=";

    let tblname = nbp.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(nbp) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseForm(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public insertUpdateFormTypes(api: string, act: string, acttype: IceTypes.ACCOUNT_TYPE, nbkey: string, nbptypes: notebookpagetotype[]): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Form service is not configured.";
    if (nbptypes == null || nbptypes.length == 0) throw "Form Types object invalid.";
    if (nbkey == null || nbkey.length == 0) throw "Form Key input invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";
    if (api == null || api.length == 0) throw "API input invalid.";

    let url = envs[currentEnv].icemedx_api + "/" + api + "/default.aspx?domain=" + act + "&data=formtypes&domaintype=" + acttype.toString() + "&key=" + nbkey + "&subkeys=";

    let tblname = nbptypes[0].gettablename().toLowerCase();

    let alldata = [];
    for (let ityp = 0; ityp < nbptypes.length; ityp++) {
      let typ = nbptypes[ityp];
      let typdata = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(typ) + "}";
      alldata.push(typdata);
    }

    let data = "[" + alldata.join(",") + "]";

    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseFormType(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  private parseForms(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["NOTEBOOK_PAGE_INDEX"]);
  }

  private parseFormType(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["NOTEBOOK_PAGE_TO_TYPE"]);
  }

  parseForm(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["NOTEBOOK_PAGE"]);
  }

}
